import {
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Dropdown } from 'primeng/dropdown';
import { MxBaseComponent } from 'src/app/core/base/mx.base-component';

@Component({ standalone: false,
  selector: 'mx-dropdown',
  templateUrl: './mx.dropdown.component.html',
  styleUrls: ['./mx.dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MxDropdownComponent),
      multi: true,
    },
  ],
})
export class MxDropdownComponent
  extends MxBaseComponent
  implements ControlValueAccessor
{
  @ViewChild('dropdown') dropdown: Dropdown;

  /**
   * Identifier of the input element.
   */
  @HostBinding('attr.id')
  externalId = '';

  @Input()
  set id(value: string) {
    this._id = value;
    this.externalId = null;
  }

  get id() {
    return this._id;
  }

  private _id = '';

  /**
   * Name of the input element.
   */
  @HostBinding('attr.name')
  externalName = '';

  @Input()
  set name(value: string) {
    this._name = value;
    this.externalName = null;
  }

  get inputName() {
    return this._name;
  }

  private _name = '';

  /**
   * Index of the element in tabbing order.
   */
  @HostBinding('attr.tabindex')
  externalTabindex = '';

  @Input()
  set tabindex(value: number) {
    this._tabindex = value;
    this.externalTabindex = null;
  }

  get tabindex() {
    return this._tabindex;
  }

  private _tabindex: number = null;

  /**
   * Value of the input element.
   */
  @Input('value') _value: any = null;
  onChange: any = () => {};
  onTouched: any = () => {};

  get value() {
    return this._value;
  }

  set value(val) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  /**
   * Component is visible
   */
  @Input() visible: boolean = true;

  /**
   * 	An array of objects to display as the available options.
   */
  @Input() options: any;

  /**
   * 	Name of the label field of an option.
   */
  @Input() optionLabel: string = 'label';

  /**
   * Name of the value field of an option.
   */
  @Input() optionValue: string = 'value';

  /**
   * Name of the disabled field of an option.
   */
  @Input() optionDisabled: string = 'disabled';

  /**
   * Height of the viewport in pixels, a scrollbar is defined if height of list exceeds this value.
   */
  @Input() scrollHeight: string = '200px';

  /**
   * Inline style of the element.
   */
  @Input() style: any;

  /**
   * Inline style of the overlay panel element.
   */
  @Input() panelStyle: any;

  /**
   * Style class of the element.
   */
  @Input() styleClass: any;

  /**
   * When specified, displays an input field to filter the items on keyup.
   */
  @Input() filter: boolean = true;

  /**
   * 	When specified, filter displays with this value.
   */
  @Input() filterValue: string;

  /**
   * When filtering is enabled, filterBy decides which field or fields (comma separated) to search against.
   */
  @Input() filterBy: string = 'label';

  /**
   * Defines how the items are filtered, valid values are "contains" (default) "startsWith", "endsWith", "equals", "notEquals", "in", "lt", "lte", "gt" and "gte".
   */
  @Input() filterMatchMode: 'contains' | 'startsWith' | 'endsWith' | 'equals' | 'notEquals' | 'in' | 'lt' | 'lte' | 'gt' | 'gte' = 'contains';

  /**
   * When present, it specifies that the component should be disabled.
   */
  @Input() disabled: boolean = false;

  /**
   * When present, it specifies that the component cannot be edited.
   */
  @Input() readonly: boolean = false;

  /**
   * Text to display when there is no data.
   */
  @Input() emptyMessage: string = 'No_records_found';

  /**
   * Text to display when filtering does not return any results.
   */
  @Input() emptyFilterMessage: string = 'No_records_found';

  /**
   * Target element to attach the overlay, valid values are "body" or a local ng-template variable of another element (note: use binding with brackets for template variables, e.g. [appendTo]="mydiv" for a div element having #mydiv as variable name).
   */
  @Input() appendTo: string = 'body';

  /**
   * Default text to display when no option is selected.
   */
  @Input() placeholder: string = 'Select_please';

  /**
   * A property to uniquely identify a value in options.
   */
  @Input() dataKey: string;

  /**
   * Icon class of the dropdown icon.
   */
  @Input() dropdownIcon: string = 'fa fa-chevron-down fa-fw';

  /**
   * When enabled, a clear icon is displayed to clear the value.
   */
  @Input() showClear: boolean = true;

  /**
   * Use selected item template
   */
  @Input() useSelectedItemTemplate: boolean;

  /**
   * Use item template
   */
  @Input() useItemTemplate: boolean;

  @ContentChild('selectedItemTemplateRef', { static: false })
  selectedItemTemplateRef: TemplateRef<any>;
  @ContentChild('itemTemplateRef', { static: false })
  itemTemplateRef: TemplateRef<any>;

  /**
   * Callback to invoke when component is clicked.
   */
  @Output() mxOnClick: EventEmitter<any> = new EventEmitter<any>();

  /**
   * 	Callback to invoke when value of dropdown changes.
   */
  @Output() mxOnChange: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Callback to invoke when data is filtered.
   */
  @Output() mxOnFilter: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Callback to invoke when dropdown gets focus.
   */
  @Output() mxOnFocus: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Callback to invoke when dropdown loses focus.
   */
  @Output() mxOnBlur: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Callback to invoke when dropdown overlay gets visible.
   */
  @Output() mxOnShow: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Callback to invoke when dropdown overlay gets hidden.
   */
  @Output() mxOnHide: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Callback to invoke when dropdown clears the value.
   */
  @Output() mxOnClear: EventEmitter<any> = new EventEmitter<any>();

  constructor() {
    super();
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  writeValue(value: any) {
    this.value = value || null;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  mxClick(event: any) {
    this.mxOnClick.emit(event);
  }

  mxChange(event: any) {
    this.mxOnChange.emit(event);
  }

  mxFilter(event: any) {
    this.mxOnFilter.emit(event);
  }

  mxFocus(event: any) {
    this.mxOnFocus.emit(event);
  }

  mxBlur(event: any) {
    this.mxOnBlur.emit(event);
  }

  mxShow(event: any) {
    this.mxOnShow.emit(event);
  }

  mxHide(event: any) {
    this.mxOnHide.emit(event);
  }

  mxClear(event: any) {
    this.mxOnClear.emit(event);
  }

  /* Resets filtering. */
  resetFilter() {
    this.dropdown.resetFilter();
  }

  /* Applies focus. */
  focus() {
    this.dropdown.focus();
  }

  /* Hides the panel. */
  show() {
    this.dropdown.show();
  }

  /* Hides the panel. */
  hide() {
    this.dropdown.hide();
  }
}
